import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import {BenchlingExpressionType} from 'benchling-expressions/BenchlingExpressionTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import notifications from 'notifications';
import {SupportedRoutingSchemasQuery} from 'processes/modals/router-config/__generated/SupportedRoutingSchemasQuery';
import {
  sharedStyles,
  ConditionalLogicAllowedValuesBaseProps,
} from 'processes/modals/router-config/RouterConfigConstants';
import FieldDefinitionInputOrDisplay from 'registry/field/FieldDefinitionInputOrDisplay';
import {getFieldOptions} from 'registry/field/FieldTableOptionGetters';
import {
  FieldType,
  FieldOption,
  LeafFieldOption,
  isFieldOptionSubmenu,
  isFieldOptionLeafField,
  requiredLinkToSchemaId,
} from 'schemas/fields/SchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getTagSchemaSummaryGraphQLFromListingWithFields} from 'tag-schema/util/TagSchemaUtil';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

export const SUPPORTED_ROUTING_SCHEMAS_QUERY: DocumentNode = gql`
  query SupportedRoutingSchemasQuery @skipAudit @engComponent(name: WORKFLOWS) {
    ...AllStorableSchemasGraphQL
    ...AllTagSchemasGraphQL
    ...AllAssaySchemasGraphQL
  }
  ${getFragmentWrapper('AllStorableSchemas').fragment}
  ${getFragmentWrapper('AllTagSchemas').fragment}
  ${getFragmentWrapper('AllAssaySchemas').fragment}
`;

export interface ConditionalLogicAllowedValuesProps extends ConditionalLogicAllowedValuesBaseProps {
  dataTestAttrs?: DataTestAttrs;
}

function getSchemaIdFromFieldOption(fieldOption: FieldOption): string | null {
  if (!isFieldOptionLeafField(fieldOption)) {
    return null;
  }

  return requiredLinkToSchemaId(fieldOption.requiredLink);
}

function getLeafFieldOptionFromSchemaId(
  schemaId: string,
  fieldOptions: Array<FieldOption>
): LeafFieldOption | null {
  for (const fieldOption of fieldOptions) {
    if (isFieldOptionSubmenu(fieldOption)) {
      for (const subOption of fieldOption.submenu) {
        if (isFieldOptionLeafField(subOption) && getSchemaIdFromFieldOption(subOption) === schemaId) {
          return subOption;
        }
      }
    }
  }
  return null;
}

export default function ConditionalLogicSchemaDropdown(
  props: ConditionalLogicAllowedValuesProps
): JSX.Element | null {
  const result = useBenchlingQuery<SupportedRoutingSchemasQuery>(SUPPORTED_ROUTING_SCHEMAS_QUERY);

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load possible values; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const coalescedData = {
      storableSchemas: [
        ...result.data.boxSchemas,
        ...result.data.containerSchemas,
        ...result.data.locationSchemas,
        ...result.data.plateSchemas,
      ],
      tagSchemas: result.data.tagSchemas,
      assaySchemas: [...result.data.requestSchemas, ...result.data.resultSchemas, ...result.data.runSchemas],
    };

    const tryGetExpressionValue = (value: unknown): string | null => {
      if (!value) {
        return null;
      }

      if (typeof value === 'string' && APIUtil.apiIdToMaybeType(value)) {
        return value;
      }

      return null;
    };

    const availableFieldOptions: Array<FieldOption> = getFieldOptions({
      schemaFieldSelectors: [],
      storableSchemas: graphQLArrayToREST('StorableSchema', coalescedData.storableSchemas),
      tagSchemas: coalescedData.tagSchemas.map((ts) => getTagSchemaSummaryGraphQLFromListingWithFields(ts)),
      assaySchemas: graphQLArrayToREST('AssaySchema', coalescedData.assaySchemas),
      includeEntrySchemas: true,
      includeSnapshotField: false,
      includeBooleanField: false,
      includeJSONField: false,
      includeAssayLinks: true,
    }).filter(
      // only care about options that have at least one submenu item with a schema id
      (fieldOption: FieldOption) =>
        isFieldOptionSubmenu(fieldOption) &&
        fieldOption.submenu.some((subOption: FieldOption) => getSchemaIdFromFieldOption(subOption) != null)
    );

    availableFieldOptions.forEach((fieldOption: FieldOption) => {
      if (isFieldOptionSubmenu(fieldOption)) {
        fieldOption.submenu = fieldOption.submenu.filter(
          (subOption: FieldOption) => getSchemaIdFromFieldOption(subOption) != null
        );
      }
    });

    const selectedSchemaApiId = tryGetExpressionValue(props.constantExpression?.value);
    const selectedFieldOptionType =
      selectedSchemaApiId != null
        ? getLeafFieldOptionFromSchemaId(selectedSchemaApiId, availableFieldOptions)
        : null;

    let selectedFieldType = FieldType.BIOENTITY_LINK;
    if (selectedFieldOptionType?.fieldDefinition) {
      // We should be guaranteed that our selected field type is not computed because we didn't
      // request snapshot fields above. Assert our fieldDefinition is in the FieldType enum
      assert(
        Object.values(FieldType).includes(selectedFieldOptionType?.fieldDefinition as FieldType),
        'Field type must not be computed'
      );
      selectedFieldType = selectedFieldOptionType?.fieldDefinition as FieldType;
    }

    // This field will never be submitted, we only care about the field type
    // TODO(BNCH-70974): Refactor the child components to not use a schema field
    const currentFieldState: SchemaField = {
      id: 0,
      dbId: 0,
      apiId: '',
      name: '',
      systemName: '',
      displayName: '',
      fieldType: selectedFieldType,
      isRequired: false,
      isMulti: false,
      isComputed: false,
      isConvertedFromLink: false,
      isSnapshot: false,
      isIntegration: false,
      isParentLink: false,
      requiredLink: selectedFieldOptionType?.requiredLink || null,
      archiveRecord: null,
      isUneditable: false,
    };

    return (
      <FieldDefinitionInputOrDisplay
        dropdownClassName={css(sharedStyles.routerInputControl)}
        shouldEllipsize={true}
        isFieldBeingCreated={false}
        availableFieldOptions={availableFieldOptions}
        schemaFieldSelectors={[]}
        currentFieldState={currentFieldState}
        disableRightSidePadding={true}
        inEditMode={true}
        onSelectFieldOption={(fieldOption) => {
          const newParam = {
            name: props.expression?.parameters?.[1]?.name,
            expression: {
              type: BenchlingExpressionType.CONSTANT_VALUE,
              itemType: {
                name: LeafType.TEXT,
              },
              value: getSchemaIdFromFieldOption(fieldOption),
            },
          };
          const newExpression = {
            ...props.expression,
            parameters: [props.expression?.parameters?.[0], newParam],
          };

          props.setExpression(newExpression);
        }}
        dataTestAttrs={{component: 'ConditionalLogicSchemaDropdown', element: 'root'}}
      />
    );
  }
  return null;
}
